import { useMemo } from 'react'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { formatNumber, formatPercent } from '@/lib/format-utils'
import { cn } from '@/lib/utils'
import { usePackageNameGetter } from '@/lib/package-utils'

/**
 * 展示 pid=14 的详细 ROAS 表格
 * 行是 package_name，列是 country (JP)
 */
const DayRoas14Table = ({ data, dayValue }) => {
  const getPackageName = usePackageNameGetter()
  // 处理数据：按 package_name 分组作为行
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return []

    // 获取所有唯一的 package_name
    const uniquePackageNames = [
      ...new Set(data.map((d) => d.packageName).filter(Boolean)),
    ].sort()

    // 构建表格数据：每行是一个 package_name
    return uniquePackageNames.map((packageName) => ({
      packageName,
    }))
  }, [data])

  // 构建列：第一列是 package_name，后续列是每个 country
  const columns = useMemo(() => {
    if (!data || data.length === 0) return []

    // 获取所有唯一的 country
    const uniqueCountries = [
      ...new Set(data.map((d) => d.country).filter(Boolean)),
    ].sort()

    // Package Name 列
    const packageColumn = {
      id: 'packageName',
      header: 'Package Name',
      accessorFn: (row) => row.packageName,
      cell: (row) => {
        const packageCode = row.getValue()
        const packageName = getPackageName(packageCode)
        return (
          <div className={cn('font-medium whitespace-nowrap')}>
            {packageName}
          </div>
        )
      },
      meta: {
        sticky: true,
        stickyLeft: 'left-[0px]',
      },
    }

    // 为每个 country 创建一列
    const countryColumns = uniqueCountries.map((country) => ({
      id: `country-${country}`,
      header: country.toUpperCase(),
      // 设置宽度为 100% 以填满剩余空间
      size: 1000,
      accessorFn: (row) => {
        const packageName = row.packageName
        const matchedData = data.filter(
          (d) => d.country === country && d.packageName === packageName,
        )

        if (matchedData.length === 0) return null

        // 如果有多条数据，汇总
        if (matchedData.length === 1) {
          return matchedData[0]
        }

        // 汇总多条数据
        const totalRevenue = matchedData.reduce(
          (sum, d) => sum + d.total_revenue,
          0,
        )
        const totalCost = matchedData.reduce((sum, d) => sum + d.total_cost, 0)
        const avgRoas = totalCost > 0 ? totalRevenue / totalCost : 0

        const totalDays = matchedData.reduce((sum, d) => sum + d.days_count, 0)
        const uniqueDays = new Set(matchedData.map((d) => d.days_count)).size

        const dailyRevenue =
          uniqueDays > 0 ? totalRevenue / (totalDays / matchedData.length) : 0
        const dailyCost =
          uniqueDays > 0 ? totalCost / (totalDays / matchedData.length) : 0

        return {
          avg_day0_roas: avgRoas,
          daily_revenue: dailyRevenue,
          daily_cost: dailyCost,
          total_revenue: totalRevenue,
          total_cost: totalCost,
          days_count: totalDays,
        }
      },
      cell: (row) => {
        const roasData = row.getValue()
        if (!roasData) {
          return <div className="text-gray-400 text-center">-</div>
        }

        const roas = roasData.avg_day0_roas
        const dailyRevenue = roasData.daily_revenue
        const dailyCost = roasData.daily_cost

        return (
          <div className="text-right">
            <div
              className={cn(
                'font-bold',
                roas >= 1.0
                  ? 'text-green-600'
                  : roas >= 0.5
                  ? 'text-yellow-600'
                  : 'text-red-600',
              )}
            >
              {formatPercent(roas, 1)}
            </div>
            <div className="text-xs text-gray-500">
              ${formatNumber(dailyRevenue, 0)} / ${formatNumber(dailyCost, 0)}
            </div>
          </div>
        )
      },
    }))

    return [packageColumn, ...countryColumns]
  }, [data, getPackageName])

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (!data || data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-gray-400">
        暂无数据
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-gray-600">
        Day {dayValue} ROAS - PID 14 (按 Package Name 分组)
      </div>
      <DataTableWithBorder table={table} />
    </div>
  )
}

export default DayRoas14Table
